/**
 * @file page-breadcrumbs.ts
 * @description Хлебные крошки страницы и сайта словами владельца
 * (phase19.16, партия «SEO-техника», v0.19.65; SB146; макеты
 * uiux-seo-tech.md §3.3, §3.4). Чистые функции: поле «Хлебные крошки» в
 * «Настройках страницы» и вкладка «Крошки» панели «Весь сайт → Меню» читают
 * один реестр, а не держат свои подписи.
 *
 * Выбор страницы — `INHERIT | SHOW | HIDE` (shared `SITE_PAGE_BREADCRUMBS`);
 * решение «показывать ли» на витрине принимает shared `showBreadcrumbs`,
 * здесь — только подписи и «сейчас на сайте» для кабинета.
 */

import { SITE_PAGE_BREADCRUMBS, type SitePageBreadcrumbs } from "@chaiberi/shared";

import type { SitePageListItem } from "../api-site-pages";

/** Варианты сегмента в «Настройках страницы». */
export const PAGE_BREADCRUMBS_LABELS: Readonly<Record<SitePageBreadcrumbs, string>> = {
  INHERIT: "Как на сайте",
  SHOW: "Показывать",
  HIDE: "Скрыть",
};

export const PAGE_BREADCRUMBS_OPTIONS = SITE_PAGE_BREADCRUMBS.map((value) => ({ value, label: PAGE_BREADCRUMBS_LABELS[value] }));

/** Своя настройка страницы в списке вкладки «Крошки»: «показывать» / «скрыть». */
export const PAGE_BREADCRUMBS_OWN: Readonly<Record<Exclude<SitePageBreadcrumbs, "INHERIT">, string>> = {
  SHOW: "показывать",
  HIDE: "скрыть",
};

/** Страницы конструктора со своей настройкой крошек (не «как на сайте»), без удалённых. */
export function pagesWithOwnBreadcrumbs(list: readonly SitePageListItem[] | null): SitePageListItem[] {
  return (list ?? []).filter((p) => p.kind === "CUSTOM" && !p.deletedAt && p.breadcrumbs !== undefined && p.breadcrumbs !== "INHERIT");
}

/** Видны ли крошки на странице при выборе страницы и переключателе сайта. */
export function pageShowsBreadcrumbs(mode: SitePageBreadcrumbs, siteOn: boolean): boolean {
  return mode === "INHERIT" ? siteOn : mode === "SHOW";
}

/** Своя настройка страницы словами: «показываются» / «скрыты»; «как на сайте» — null. */
export function pageOwnBreadcrumbsWord(mode: SitePageBreadcrumbs): string | null {
  return mode === "INHERIT" ? null : breadcrumbsWord(mode === "SHOW");
}

/** «показываются» / «скрыты». */
export const breadcrumbsWord = (shown: boolean): string => (shown ? "показываются" : "скрыты");

/** Добавка к сводке группы «Для поиска»: только когда выбор не «как на сайте». */
export function pageBreadcrumbsSummary(mode: SitePageBreadcrumbs): string {
  if (mode === "SHOW") return " · крошки показаны";
  if (mode === "HIDE") return " · крошки скрыты";
  return "";
}

/** Образец «Так на сайте» во вкладке «Крошки». */
export const BREADCRUMBS_SAMPLE_ON = "Главная / Каталог / Сборы трав";
export const BREADCRUMBS_SAMPLE_OFF = "Крошки скрыты на всех страницах, кроме страниц со своей настройкой «показывать» и страницы товара.";

export const BREADCRUMBS_INTRO = "Хлебные крошки — строка над заголовком страницы. Помогает вернуться на уровень выше; Яндекс показывает её в поиске.";
export const BREADCRUMBS_SWITCH_HINT = "На страницах сайта, в каталоге и категориях, в блоге и статьях, в юридических документах.";
export const BREADCRUMBS_PRODUCT_NOTE = "На странице товара крошками управляет элемент «Хлебные крошки» шаблона товара.";
export const BREADCRUMBS_NEVER_NOTE = "На главной и при оформлении заказа крошек нет никогда.";
export const BREADCRUMBS_PAGE_NOTE = "Отдельной странице можно выбрать в её «Настройках страницы → Для поиска».";
export const BREADCRUMBS_MARKUP_NOTE = "Разметка для поиска выводится, только когда крошки видны.";
export const BREADCRUMBS_HOME_NOTE = "На главной крошек нет никогда.";
